// Shared pixel-art icon helpers for the DOM UI (ability bar + menu).

// Draw a pixel-art icon grid ('x' = filled) into a small crisp canvas, tinted by color.
export function makeIconCanvas(grid: string[], color: number, scale = 2): HTMLCanvasElement {
  const rows = grid.length;
  const cols = grid[0].length;
  const c = document.createElement('canvas');
  c.width = cols * scale;
  c.height = rows * scale;
  const ctx = c.getContext('2d');
  if (ctx) {
    ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    for (let y = 0; y < rows; y++) {
      const row = grid[y];
      for (let x = 0; x < row.length; x++) {
        if (row[x] === 'x') ctx.fillRect(x * scale, y * scale, scale, scale);
      }
    }
  }
  c.style.width = `${cols * scale}px`;
  c.style.height = `${rows * scale}px`;
  c.style.imageRendering = 'pixelated';
  return c;
}

// Draw a multi-color pixel-art grid into a crisp canvas. Each char maps to a CSS color
// via `palette`; chars missing from the palette (e.g. '.') are transparent.
export function drawPixels(grid: string[], palette: Record<string, string>, scale = 3): HTMLCanvasElement {
  const rows = grid.length;
  let cols = 0;
  for (const r of grid) cols = Math.max(cols, r.length);
  const c = document.createElement('canvas');
  c.width = cols * scale;
  c.height = rows * scale;
  const ctx = c.getContext('2d');
  if (ctx) {
    for (let y = 0; y < rows; y++) {
      const row = grid[y];
      for (let x = 0; x < row.length; x++) {
        const col = palette[row[x]];
        if (col) {
          ctx.fillStyle = col;
          ctx.fillRect(x * scale, y * scale, scale, scale);
        }
      }
    }
  }
  c.style.width = `${cols * scale}px`;
  c.style.height = `${rows * scale}px`;
  c.style.imageRendering = 'pixelated';
  return c;
}

// Symbolic pixel icons per ability (keys match ABILITIES). 'x' = filled.
// prettier-ignore
export const ABILITY_ICONS: Record<string, string[]> = {
  berserk: [
    '....x....',
    '....x....',
    '....x....',
    '....x....',
    '....x....',
    '..xxxxx..',
    '....x....',
    '...xxx...',
    '....x....',
  ],
  cryo: [
    '....x....',
    '.x..x..x.',
    '..x.x.x..',
    '...xxx...',
    'xxxxxxxxx',
    '...xxx...',
    '..x.x.x..',
    '.x..x..x.',
    '....x....',
  ],
  transmute: [
    '....x....',
    '...xxx...',
    '..xxxxx..',
    '.xxxxxxx.',
    'xxxxxxxxx',
    '.xxxxxxx.',
    '..xxxxx..',
    '...xxx...',
    '....x....',
  ],
  aegis: [
    '.xxxxxxx.',
    '.xxxxxxx.',
    '.xxxxxxx.',
    '.xxxxxxx.',
    '..xxxxx..',
    '..xxxxx..',
    '...xxx...',
    '....x....',
    '.........',
  ],
  blackhole: [
    '..xxxxx..',
    '.xx...xx.',
    '.x.....x.',
    'xx.....xx',
    'xx.....xx',
    'xx.....xx',
    '.x.....x.',
    '.xx...xx.',
    '..xxxxx..',
  ],
  tempest: [
    '.........',
    '.........',
    '..x...x..',
    '...x...x.',
    '....x...x',
    '...x...x.',
    '..x...x..',
    '.........',
    '.........',
  ],
  meltdown: [
    '....x....',
    '....x....',
    '...xxx...',
    '...xxx...',
    '..xxxxx..',
    '..xxxxx..',
    '.xxxxxxx.',
    '.xxxxxxx.',
    '..xxxxx..',
  ],
  overload: [
    '.....xx..',
    '....xx...',
    '...xx....',
    '..xxxxx..',
    '.....xx..',
    '....xx...',
    '...xx....',
    '..xx.....',
    '.........',
  ],
  thornskin: [
    'x...x...x',
    '.x..x..x.',
    '..xxxxx..',
    '..x...x..',
    'xxx...xxx',
    '..x...x..',
    '..xxxxx..',
    '.x..x..x.',
    'x...x...x',
  ],
  rootSnare: [
    '....x....',
    '..x.x.x..',
    '...xxx...',
    'xxxxxxxxx',
    '...xxx...',
    '..x.x.x..',
    '.x..x..x.',
    'x...x...x',
    '.........',
  ],
  sporeburst: [
    'x..x.x..x',
    '.........',
    '..xxxxx..',
    '..xxxxx..',
    'x.xxxxx.x',
    '..xxxxx..',
    '..xxxxx..',
    '.........',
    'x..x.x..x',
  ],
  nectarWard: [
    '...xxx...',
    '..xxxxx..',
    '.xxxxxxx.',
    '.xxxxxxx.',
    '..xxxxx..',
    '..xxxxx..',
    '...xxx...',
    '....x....',
    '....x....',
  ],
  mycelialGrasp: [
    'xx.....xx',
    '.xx...xx.',
    '..x...x..',
    '..xxxxx..',
    '.xx.x.xx.',
    'xx..x..xx',
    '....x....',
    '...x.x...',
    '..x...x..',
  ],
  fireflyRush: [
    'x........',
    '...x.....',
    '.x..xx...',
    '.....xx..',
    '..x....xx',
    '....xx...',
    '.x.....x.',
    '...x.....',
    '......x..',
  ],
  blightWave: [
    '....x....',
    '..x.xxx..',
    '...xxx...',
    '.xxxxxxx.',
    'xxxxxxxxx',
    '.xxxxxxx.',
    '..xxxxx..',
    '...xxx...',
    '..x...x..',
  ],
  harvestSwarm: [
    'x..x.x..x',
    '.x..x..x.',
    '..x...x..',
    'x..xxx..x',
    '.xxx.xxx.',
    'x..xxx..x',
    '..x...x..',
    '.x..x..x.',
    'x..x.x..x',
  ],
  meteorMantle: [
    'x........',
    '.x.......',
    '..x.xxx..',
    '...xxxxx.',
    '..xxxxxxx',
    '..xxxxxxx',
    '...xxxxx.',
    '....xxx..',
    '.....x...',
  ],
  timeLock: [
    '..xxxxx..',
    '.x..x..x.',
    'x...x...x',
    'x...x...x',
    'x...xxx.x',
    'x.......x',
    'x.......x',
    '.x.....x.',
    '..xxxxx..',
  ],
  prismBurst: [
    '....x....',
    'x...x...x',
    '.x..x..x.',
    '..xxxxx..',
    'xxxxxxxxx',
    '..xxxxx..',
    '.x..x..x.',
    'x...x...x',
    '....x....',
  ],
  orbitGuard: [
    '..xxxxx..',
    '.x.....x.',
    'x..xxx..x',
    'x.x...x.x',
    'x.x...x.x',
    'x.x...x.x',
    'x..xxx..x',
    '.x.....x.',
    '..xxxxx..',
  ],
  eventHorizon: [
    '....x....',
    '..x...x..',
    '.x.xxx.x.',
    '..xxxxx..',
    'xxxxxxxxx',
    '..xxxxx..',
    '.x.xxx.x.',
    '..x...x..',
    '....x....',
  ],
  railSurge: [
    'xx.......',
    '.xx......',
    '..xx..xx.',
    '...xxxx..',
    '..xxxx...',
    '.xx..xx..',
    '......xx.',
    '.......xx',
    '.........',
  ],
  solarEruption: [
    'x...x...x',
    '.x..x..x.',
    '..xxxxx..',
    '..xxxxx..',
    'xxxxxxxxx',
    '..xxxxx..',
    '..xxxxx..',
    '.x..x..x.',
    'x...x...x',
  ],
  conduitStorm: [
    '.xx...xx.',
    '..xx.xx..',
    '...xxx...',
    'xx..x..xx',
    '.xxxxxxx.',
    'xx..x..xx',
    '...xxx...',
    '..xx.xx..',
    '.xx...xx.',
  ],
  // NinjaKid Dojo jutsus.
  stoneJutsu: [
    '.........',
    '...xxx...',
    '..xxxxx..',
    '.xxxxxxx.',
    '.xxxxxxx.',
    '.xx.xxxx.',
    '..xxxxx..',
    '...xxx...',
    '.........',
  ],
  windStar: [
    '....x....',
    '...xxx...',
    'x..xxx..x',
    'xxxx.xxxx',
    '..x...x..',
    'xxxx.xxxx',
    'x..xxx..x',
    '...xxx...',
    '....x....',
  ],
  ninjaDisc: [
    '..xxxxx..',
    '.xx...xx.',
    'xx.....xx',
    'x...x...x',
    'x..xxx..x',
    'x...x...x',
    'xx.....xx',
    '.xx...xx.',
    '..xxxxx..',
  ],
  sexyJutsu: [
    '.........',
    '.xx...xx.',
    'xxxx.xxxx',
    'xxxxxxxxx',
    'xxxxxxxxx',
    '.xxxxxxx.',
    '..xxxxx..',
    '...xxx...',
    '....x....',
  ],
  outgame: [
    '..xxxxx..',
    '.x.....x.',
    'x..xxx..x',
    'x.x...x.x',
    'x.x.x.x.x',
    'x.x...x.x',
    'x..xxx..x',
    '.x.....x.',
    '..xxxxx..',
  ],
  blueLightning: [
    '....xxxx.',
    '...xxx...',
    '..xxx....',
    '.xxxxxxx.',
    '....xxx..',
    '...xxx...',
    '..xxx....',
    '.xxx.....',
    '.xx......',
  ],
  fireJutsu: [
    '....x....',
    '...xx....',
    '...xxx...',
    '..xxxxx..',
    '.xxxxxxx.',
    '.xx.xxxx.',
    '.xxxxxxx.',
    '..xxxxx..',
    '...xxx...',
  ],
  spiritSmash: [
    '.xx...xx.',
    '.xx...xx.',
    '.xx...xx.',
    '.xx...xx.',
    '....x....',
    '...xxx...',
    '..xxxxx..',
    '.xxxxxxx.',
    '...xxx...',
  ],
};

// Pixel speaker icons for the music toggle ('x' = filled). On = sound waves; off = an X.
// prettier-ignore
export const SPEAKER_ON: string[] = [
  '.........',
  '....x....',
  '...xx...x',
  'xxxxx.x.x',
  'xxxxx.x.x',
  'xxxxx.x.x',
  '...xx...x',
  '....x....',
  '.........',
];
// prettier-ignore
export const SPEAKER_OFF: string[] = [
  '.........',
  '....x....',
  '...xx....',
  'xxxxx.x.x',
  'xxxxx..x.',
  'xxxxx.x.x',
  '...xx....',
  '....x....',
  '.........',
];
